import InsertInvitationIcon from "@mui/icons-material/InsertInvitation";
import { FormControl, FormHelperText } from "@mui/material";
import {
  DateRangePicker,
  LocalizationProvider,
  MultiInputDateRangeField,
} from "@mui/x-date-pickers-pro";
import { AdapterLuxon } from "@mui/x-date-pickers/AdapterLuxon";
import { DateShortcutChips } from "components/DateShortcutChips";
import { DateTime } from "luxon";
import { DateShortcutType, getDateRangeShortcuts } from "util/dateRangeShortcuts";

/** The prompt for a date-scoped view that has no range to show. */
export const SELECT_DATE_RANGE_TEXT = "Select a date range to view data";

/** Shared waste date-range picker; consumers deal only in `YYYY-MM-DD` strings. */
type WasteDateRangePickerProps = {
  startDate?: string;
  endDate?: string;
  /** Fires on OK or a shortcut chip; either side can be undefined to clear. */
  onAccept: (next: { startDate?: string; endDate?: string }) => void;
  startLabel?: string;
  endLabel?: string;
  /** Show `SELECT_DATE_RANGE_TEXT` when empty. Defaults to true. */
  showHelperText?: boolean;
  /**
   * Show it when either end is missing, not only when both are: for a page
   * whose data needs both ends. Pages that accept an open-ended range leave
   * this off.
   */
  requireFullRange?: boolean;
  errorMessage?: string | null;
  minWidth?: number | string;
  shortcutType?: DateShortcutType;
};

export function WasteDateRangePicker({
  startDate,
  endDate,
  onAccept,
  startLabel = "Start Date",
  endLabel = "End Date",
  showHelperText = true,
  requireFullRange = false,
  errorMessage,
  minWidth = 300,
  shortcutType = "HISTORICAL",
}: WasteDateRangePickerProps) {
  const start = parseIsoDate(startDate);
  const end = parseIsoDate(endDate);

  const handleAccept = (next: [DateTime | null, DateTime | null]) => {
    onAccept({
      startDate: dateToIsoString(next[0]),
      endDate: dateToIsoString(next[1]),
    });
  };

  const rangeMissing = requireFullRange ? !start || !end : !start && !end;
  const helperText =
    errorMessage ?? (showHelperText && rangeMissing ? SELECT_DATE_RANGE_TEXT : null);

  return (
    <FormControl sx={{ minWidth }} size="small" variant="standard" error={Boolean(errorMessage)}>
      <LocalizationProvider dateAdapter={AdapterLuxon}>
        <DateRangePicker
          calendars={2}
          onAccept={handleAccept}
          value={[start, end]}
          localeText={{ start: startLabel, end: endLabel }}
          slots={{
            field: MultiInputDateRangeField,
            shortcuts: DateShortcutChips,
          }}
          slotProps={{
            textField: {
              size: "small",
              fullWidth: true,
              variant: "standard",
              sx: { "& .MuiInputBase-root": { height: "40px" } },
              inputProps: {
                autoComplete: "off",
                "data-1p-ignore": "true",
                "data-lpignore": "true",
                "data-form-type": "other",
              },
              InputProps: { endAdornment: <InsertInvitationIcon /> },
            },
            shortcuts: { items: getDateRangeShortcuts(shortcutType) },
          }}
        />
      </LocalizationProvider>
      {helperText && (
        <FormHelperText role={errorMessage ? "alert" : undefined}>{helperText}</FormHelperText>
      )}
    </FormControl>
  );
}

function parseIsoDate(iso?: string): DateTime | null {
  if (!iso) return null;
  const d = DateTime.fromISO(iso);
  return d.isValid ? d : null;
}

function dateToIsoString(d: DateTime | null): string | undefined {
  return d?.isValid ? (d.toISODate() ?? undefined) : undefined;
}
